/**
 * MCP servers list page — `GET /mcp-servers`.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference):
 * `ShellHeader` for the title and primary action, `CordioAbpPagedDataTable` for the grid, one
 * `DeleteDialog` driven by `useDeleteDialog`, and a `.mcp-servers-list` BEM block.
 *
 * The endpoint is genuinely paged (`pageNumber`, `pageSize`, `searchTerm`, `isActive` →
 * `PagedResult<McpServerDto>`), so `createCortexPagedDataSource` hands it straight through and the
 * grid owns paging and search outright. That replaced the page's own `filters` object, `totalCount`
 * state and the `useEffect` that refetched on six dependencies.
 *
 * `listVersion` is the one piece of refetch state kept. Three mutations bump it: delete, tool
 * discovery (changes `toolCount`) and a health check (changes `healthStatus` and
 * `lastHealthCheckAt`) — all of which the grid did not perform and cannot know about.
 *
 * Capabilities preserved outside the grid:
 *   - **Discover tools** and **Check health** stay per-row actions. They are the operational
 *     reasons this page exists — an MCP server whose tools were never discovered contributes
 *     nothing to an agent, and a red health chip is the first thing anyone looks for here. The grid
 *     has no slot for a live probe, so they live in `actions` and refresh the list on success.
 *   - Tenant / project scoping. The service reads the active scope from the request context, so the
 *     ids are `dataSource` dependencies: switching workspace or project refetches, as before.
 *
 * Not expressible in the pattern: the pre-migration page swapped each probe's icon for a spinner
 * while it was in flight. `CordioDataTableAction` has no busy or disabled state, so instead the
 * page keeps a single `busyRowId` and ignores a second click while a probe is running. The
 * feedback is the resulting notification rather than an inline spinner.
 *
 * --- Column plan -------------------------------------------------------------------------------
 * field             | header     | width | cell type            | filter
 * ------------------|------------|-------|----------------------|-------------------------------
 * name              | Name       | 24%   | text, clickable      | toolbar search -> searchTerm
 * serverUrl         | Server URL | 26%   | text, 60 + readMore  | toolbar search -> searchTerm
 * healthStatus      | Health     | 14%   | custom (health Chip) | —
 * toolCount         | Tools      | 8%    | number               | —
 * isActive          | Status     | 12%   | custom (Active Chip) | status tabs -> `isActive`
 * lastHealthCheckAt | Last check | 16%   | date, locale         | —
 *
 * Dropped from the pre-migration table: the `description` subtitle under Name. Six columns is the
 * ceiling, and a server's description is prose that belongs on its detail page — the name and URL
 * are what identify it in a list.
 *
 * Two changes worth naming: sorting is not offered, because `GET /mcp-servers` accepts no `sortBy`
 * and a sortable header that reorders nothing is worse than none; and a server that has never been
 * probed now shows an em dash rather than the word "Never", which is what the grid's date cell
 * renders for a null timestamp.
 * -----------------------------------------------------------------------------------------------
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Chip,
  CordioAbpPagedDataTable,
  ShellHeader,
  type ChipColor,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { Plus, Trash2 } from 'lucide-react';

import {
  createCortexPagedDataSource,
  type CortexPagedQuery,
} from '../components/data-display/cortexPagedDataSource';
import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import { HealthStatus, mcpService, type McpServerDto, type McpServerFilters } from '../services/mcpService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';

import './McpServersListPage.scss';

/**
 * Quick-filter tabs.
 *
 * `value` is load-bearing twice: the grid writes it to the toolbar's `paramKey`, AND it builds each
 * tab's test id as `cordio-datatable-status-tab-<value>`. GRID-13 asserts on `-all` / `-true` /
 * `-false`, so the All tab must carry the literal `'all'` — an empty value would emit
 * `cordio-datatable-status-tab-` and the checklist would select nothing. `'all'` is a UI concept
 * the API rejects, hence the synthetic {@link STATUS_TAB_PARAM_KEY} that {@link toMcpServerFilters}
 * resolves to `isActive`.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
] as const;

/** The synthetic toolbar key the status tabs write to. Never sent to the API as-is. */
const STATUS_TAB_PARAM_KEY = 'status';

/**
 * Health chip colour, keyed off the value the server reports.
 *
 * The label is always the API's own string — inventing alternate wording here would make the grid
 * and the server detail page disagree about the same field.
 */
function healthChipColor(status: string): ChipColor {
  switch (status) {
    case HealthStatus.Healthy:
      return 'green';
    case HealthStatus.Degraded:
      return 'amber';
    case HealthStatus.Unhealthy:
      return 'red';
    default:
      return 'default';
  }
}

/**
 * Turns the grid's query into the `McpServerFilters` shape `mcpService.getMcpServers` accepts.
 *
 * The synthetic status tab becomes `isActive`, and All becomes "no filter at all" rather than a
 * third state. `sortBy` / `sortDescending` are dropped: the endpoint builds its query string from
 * an explicit allow-list that has no sort keys, so passing them would be inert.
 */
function toMcpServerFilters(query: CortexPagedQuery): McpServerFilters {
  const filters: McpServerFilters = {
    pageNumber: query.pageNumber,
    pageSize: query.pageSize,
  };

  if (typeof query.searchTerm === 'string' && query.searchTerm.trim() !== '') {
    filters.searchTerm = query.searchTerm;
  }

  const status = query[STATUS_TAB_PARAM_KEY];
  if (status === 'true' || status === 'false') {
    filters.isActive = status === 'true';
  }

  return filters;
}

export const McpServersListPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);

  /** Bumped after a delete, a discovery run or a health check, to make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);

  /**
   * The row with a probe in flight, or null. Stands in for the per-action spinner the declarative
   * action list cannot render — its job is to stop a second click firing a duplicate probe.
   */
  const [busyRowId, setBusyRowId] = useState<string | null>(null);

  const { dialogRef, pendingItem, openDelete, closeDelete } = useDeleteDialog<McpServerDto>();

  const tenantId = currentTenant?.id;
  const projectId = currentProject?.id;

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<McpServerDto>((query) =>
        mcpService.getMcpServers(toMcpServerFilters(query)),
      ),
    // `listVersion` is a deliberate dependency: changing the identity of `dataSource` is the
    // documented way to make the grid reload. The tenant / project ids are here because the service
    // scopes by the active context, so a workspace switch has to refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantId, projectId, listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<McpServerDto>>>(
    () => [
      { field: 'name', header: 'Name', type: 'text', width: '24%', clickable: true },
      {
        field: 'serverUrl',
        header: 'Server URL',
        type: 'text',
        width: '26%',
        maxLength: 60,
        showReadMore: true,
      },
      {
        field: 'healthStatus',
        header: 'Health',
        type: 'custom',
        width: '14%',
        customCell: (row) => <Chip label={row.healthStatus} color={healthChipColor(row.healthStatus)} />,
      },
      { field: 'toolCount', header: 'Tools', type: 'number', width: '8%' },
      {
        field: 'isActive',
        header: 'Status',
        type: 'custom',
        width: '12%',
        // A Chip rather than the built-in boolean cell: "Active/Inactive" is the vocabulary the
        // rest of the product uses, and it matches the status tabs above the grid.
        customCell: (row) => (
          <Chip label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'green' : 'default'} />
        ),
      },
      {
        field: 'lastHealthCheckAt',
        header: 'Last check',
        type: 'date',
        width: '16%',
        dateFormat: 'locale',
      },
    ],
    [],
  );

  /**
   * Search and status tabs only — no filter popover.
   *
   * WHY: `GET /mcp-servers` accepts exactly two filters, `searchTerm` and `isActive`, and both are
   * already on screen (the search box and the tabs). A popover could only duplicate a tab or send a
   * key the endpoint's allow-list discards, which is a filter that looks like it works and does
   * nothing. So the grid renders no `cordio-datatable-filter-trigger` here.
   */
  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search MCP servers by name or URL' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'MCP server status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
    }),
    [],
  );

  const handleDiscoverTools = useCallback(
    async (server: McpServerDto) => {
      if (busyRowId) return;
      setBusyRowId(server.id);
      try {
        const result = await mcpService.discoverTools(server.id);
        addNotification(`Discovered ${result.toolsDiscovered} tools`, 'success');
        setListVersion((v) => v + 1);
      } catch (err) {
        addNotification(err instanceof Error ? err.message : 'Failed to discover tools', 'error');
      } finally {
        setBusyRowId(null);
      }
    },
    [busyRowId, addNotification],
  );

  const handleCheckHealth = useCallback(
    async (server: McpServerDto) => {
      if (busyRowId) return;
      setBusyRowId(server.id);
      try {
        const result = await mcpService.checkHealth(server.id);
        if (result.isHealthy) {
          addNotification('Server is healthy', 'success');
        } else {
          // A failed probe is not a failed request: report what the server said and keep going.
          addNotification(
            `Server health check failed: ${result.errorMessage || 'Unknown error'}`,
            'warning',
          );
        }
        setListVersion((v) => v + 1);
      } catch (err) {
        addNotification(err instanceof Error ? err.message : 'Failed to check health', 'error');
      } finally {
        setBusyRowId(null);
      }
    },
    [busyRowId, addNotification],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<McpServerDto>>>(
    () => [
      {
        icon: 'wrench',
        tooltip: 'Discover tools',
        onClick: (row) => void handleDiscoverTools(row),
      },
      {
        icon: 'activity',
        tooltip: 'Check health',
        onClick: (row) => void handleCheckHealth(row),
      },
      {
        icon: 'square-pen',
        tooltip: 'Edit MCP server',
        onClick: (row) => navigate(ROUTES.MCP_SERVER_EDIT.replace(':id', row.id)),
      },
      {
        icon: 'trash-2',
        tooltip: 'Delete MCP server',
        className: 'mcp-servers-list__action--destructive',
        onClick: (row) => openDelete(row),
      },
    ],
    [handleDiscoverTools, handleCheckHealth, navigate, openDelete],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingItem) return;

    await mcpService.deleteMcpServer(pendingItem.id);
    addNotification('MCP server deleted successfully', 'success');
    setListVersion((v) => v + 1);
  }, [pendingItem, addNotification]);

  return (
    <div className="mcp-servers-list" data-testid="mcp-server-list-page">
      <ShellHeader
        title="MCP Servers"
        description="Model Context Protocol servers this workspace can call, and the tools discovered from each."
        data-testid="mcp-server-list-header"
        actions={
          <Button
            type="button"
            variant="default"
            size="sm"
            className="gap-2"
            data-testid="add-mcp-server-btn"
            onClick={() => navigate(ROUTES.MCP_SERVER_NEW)}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Create MCP Server
          </Button>
        }
      />

      <div data-testid="mcp-server-list-grid">
        <CordioAbpPagedDataTable<McpServerDto>
          className="mcp-servers-list__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(`${ROUTES.MCP_SERVERS}/${row.id}`)}
        />
      </div>

      <DeleteDialog
        ref={dialogRef}
        title="Delete MCP server?"
        entityLabel={pendingItem?.name}
        dialogIcon={<Trash2 className="delete-dialog__icon" />}
        description="Every agent that calls a tool discovered from this server loses it, so the integrations that depend on it stop working. This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        busyLabel="Deleting…"
        onConfirm={handleDeleteConfirm}
        onCancel={closeDelete}
      />
    </div>
  );
};
